import { Board, Column, Panel } from '@gamma/ui/layout';
import { PopUpMenuLinksProps } from '@gamma/ui/navigation';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { ROUTES } from '@investigator/shared/constants';
import { useReloadHumioUI } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { LogScaleContext } from '@investigator/shared/providers';
import { hideHumioInnerNav } from '@investigator/shared/utils';
import { useContext, useEffect, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { useLocation } from 'react-router';
import { HumioLoaderOverlay, HumioUnavailable } from '../../components';
import { Dashboard } from '../../queries';

const { humioLabel, dashboardsMenu } = i18n.navigation;
const { dashboards: dashboardsRoute } = ROUTES;
const { dashboards } = i18n.pages.humio;

declare module '@chakra-ui/react' {
  interface MenuItemProps {
    'data-dashboard-name'?: string;
    'data-dashboard-id'?: string;
  }
}

/**
 * Component that displays Humio/LogScale dashboards in an iframe
 * Manages dashboard selection and navigation based on route
 */
export const HumioDashboardOverview = () => {
  const { pathname } = useLocation();

  const {
    iframeKey,
    setLogScaleDashboardParams,
    logScaleDashboardsLoading,
    logScaleDashboardsError,
    logScaleDashboardsData,
    logScaleDashboardParams,
  } = useContext(LogScaleContext);

  const [dashboardMenuLinks, setDashboardMenuLinks] = useState<
    PopUpMenuLinksProps[] | undefined
  >(undefined);
  const [searchText, setSearchText] = useState<string>('');

  /**
   * Generate menu links from dashboard data
   */
  useEffect(() => {
    if (logScaleDashboardsData?.getHumioDashboards) {
      const humioDashboards =
        logScaleDashboardsData?.getHumioDashboards?.searchDomain?.dashboards?.filter(
          (d): d is Dashboard => d !== null,
        ) ?? [];
      const humioMenuLinks = humioDashboards
        .filter((d): d is Dashboard => d !== null)
        .sort((a, b) => (a.name < b.name ? -1 : 1))
        .map((link) => {
          return {
            to: dashboardsRoute(),
            label: link.name,
            menuItemProps: {
              'data-dashboard-name': link.name,
              'data-dashboard-id': link.id,
            },
          };
        });

      setDashboardMenuLinks([
        { to: dashboardsRoute(), label: dashboardsMenu, isHidden: true },
        ...humioMenuLinks,
      ]);
    }
  }, [logScaleDashboardsData]);

  /**
   * Set search text based on current route
   */
  useEffect(() => {
    if (pathname === ROUTES.networkOverview) {
      setSearchText(humioLabel.networkOverview);
    } else if (pathname === ROUTES.securityPosture) {
      setSearchText(humioLabel.securityPosture);
    } else if (pathname === ROUTES.threatHunting) {
      setSearchText(humioLabel.threatHunting);
    }
  }, [pathname]);

  /**
   * Update dashboard parameters when search text or menu links change
   */
  useEffect(() => {
    const selectedDashboard = dashboardMenuLinks?.find((dashboardLink) =>
      dashboardLink.label.toLowerCase().includes(searchText.toLowerCase()),
    );
    if (selectedDashboard) {
      const dashboardName =
        selectedDashboard.menuItemProps?.['data-dashboard-name'] ?? '';
      const dashboardId =
        selectedDashboard.menuItemProps?.['data-dashboard-id'] ?? '';

      setLogScaleDashboardParams?.({
        name: dashboardName,
        dashboardId: dashboardId,
      });
    } else {
      setLogScaleDashboardParams?.({ name: searchText });
    }
  }, [searchText, dashboardMenuLinks, setLogScaleDashboardParams]);

  const { name, dashboardId, investigatorApp, corelightDashboard } =
    (logScaleDashboardParams ?? {}) as {
      name?: string;
      dashboardId?: string;
      investigatorApp?: string;
      corelightDashboard?: string;
    };

  const dashboardDetails = {
    name,
    dashboardId,
    investigatorApp,
    corelightDashboard,
  };

  const { iframeRef, iframeSrc } = useReloadHumioUI({
    iframeType: 'dashboards',
    dashboardDetails,
  });

  localStorage.removeItem('dashboard_link');

  // Handle error or no dashboards available
  if (
    logScaleDashboardsError ||
    (logScaleDashboardsData &&
      !logScaleDashboardsData.getHumioDashboards?.searchDomain?.dashboards) ||
    (logScaleDashboardsData &&
      logScaleDashboardsData.getHumioDashboards?.searchDomain?.dashboards
        ?.length === 0)
  ) {
    return (
      <HumioUnavailable
        unavailable={dashboards.unavailable}
        techSupport={dashboards.techSupport}
      />
    );
  } else if (logScaleDashboardsLoading || !logScaleDashboardsData) {
    // Loading and data can become out of sync so show loading screen
    return (
      <ErrorBoundary page="Search" styleClass="main">
        <Panel>
          <GraphQLReqStatus
            loading={
              Boolean(logScaleDashboardsLoading) || !logScaleDashboardsData
            }
          />
        </Panel>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary page="Humio Dashboards Overview" styleClass="main">
      <Board
        h="100%"
        flex="1 1 auto"
        flexDirection="column"
        data-testid="humio-dashboards-overview"
        margin={0}
        width="auto"
      >
        <Column
          mt={1}
          pb={0}
          flexGrow={1}
          display="flex"
          flexBasis="auto"
          overflow="hidden"
          flexDirection="column"
        >
          {isSafari && <HumioLoaderOverlay reloadKey={iframeKey} />}
          {iframeSrc && (
            <iframe
              title="ui"
              width="100%"
              id="humio-ui"
              data-testid="humio-ui"
              height="calc(100% + 64px)"
              style={{
                flexGrow: 1,
                display: 'flex',
                paddingBottom: 0,
              }}
              key={iframeKey}
              ref={iframeRef}
              src={iframeSrc}
              onLoad={() => hideHumioInnerNav()}
            />
          )}
        </Column>
      </Board>
    </ErrorBoundary>
  );
};
